import React from 'react';
import {useTranslation} from 'react-i18next';
import {AlertCircle, RefreshCcw} from 'lucide-react';
import {cn} from '@/shared/utils/utils.ts';
import {Button} from './Button';
import {motion} from 'framer-motion';

interface ErrorViewProps {
  title?: string;
  message?: string;
  onRetry?: (() => void) | undefined;
  className?: string;
  variant?: 'default' | 'card' | 'fullscreen';
  icon?: React.ElementType;
  isLoading?: boolean;
}

/**
 * ErrorView Component
 *
 * Standardized error display with icon, title, message, and retry action.
 * Supports multiple variants for different contexts.
 */
export const ErrorView: React.FC<ErrorViewProps> = ({
  title,
  message,
  onRetry,
  className,
  variant = 'default',
  icon: Icon = AlertCircle,
  isLoading,
}) => {
  const { t } = useTranslation();
  const defaultTitle = title ?? t('ui.errorview.something_went_wrong');
  const isFullscreen = variant === 'fullscreen';
  const isCard = variant === 'card';

  const containerClasses = cn(
    'flex flex-col items-center justify-center text-center p-6 animate-fade-in',
    isFullscreen && 'min-h-[60vh]',
    isCard && 'bg-card border border-destructive/20 rounded-apple-xl shadow-sm',
    className
  );

  return (
    <motion.div
      className={containerClasses}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="mb-4 rounded-full bg-destructive/10 p-3">
        {React.createElement(Icon, { className: "h-8 w-8 text-destructive motion-safe:animate-pulse" })}
      </div>

      <h3 className="mb-2 text-body-lg font-semibold text-foreground">
        {defaultTitle}
      </h3>

      {message && (
        <p className="mb-6 max-w-md text-body-sm-responsive text-muted-foreground text-pretty">
          {message}
        </p>
      )}

      {onRetry && (
        <Button
          variant="outline"
          onClick={onRetry}
          disabled={isLoading}
        >
          <RefreshCcw className="h-4 w-4" />
          {t('ui.errorview.try_again')}
        </Button>
      )}
    </motion.div>
  );
};
